/**
 * This hook provides functionalities to manage owner data, including fetching,
 * creating, updating, and deleting owners.
 */

import { useCallback, useEffect, useState } from 'react';
import useRole from 'src/features/auth/hooks/useUserRoles';
import { IOwner, IOwnerRequest, IOwnerUpdateRequest } from 'src/features/owner/ts/interfaces/Owner';
import { useSnackbarProvider } from 'src/hooks';
import { RoleEnum } from 'src/ts/enums';
import { RoleType } from 'src/ts/interfaces';
import ownerApi from '../api';

interface UseOwnerReturn {
  // State
  owner: IOwner | null;
  isLoading: boolean;
  error: string | null;
  isCreating: boolean;
  isUpdating: boolean;
  createSuccess: boolean;
  updateSuccess: boolean;
  allowCreateOwners: () => boolean;

  // Actions
  onCreate: (ownerData: IOwnerRequest) => Promise<void>;
  onUpdate: (id: string, ownerData: IOwnerUpdateRequest) => Promise<void>;
  // onGetById: (id: string) => Promise<void>;
  onResetCreate: () => void;
  onResetUpdate: () => void;
  onReset: () => void;
}

const useOwner = (id: string | null = null): UseOwnerReturn => {
  // State management
  const [owner, setOwner] = useState<IOwner | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { role } = useRole();
  const allowCreateOwners = () => {
    return [RoleEnum.Admin, RoleEnum.AccountRepresentative].includes(role as RoleType);
  };

  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();
  // Reset functions
  const onResetCreate = useCallback(() => {
    setIsCreating(false);
    setCreateSuccess(false);
    setError(null);
  }, []);
  const onResetUpdate = useCallback(() => {
    setIsUpdating(false);
    setUpdateSuccess(false);
    setError(null);
  }, []);
  const onReset = useCallback(() => {
    setOwner(null);
    setIsLoading(false);
    setError(null);
    setIsCreating(false);
    setIsUpdating(false);
    setCreateSuccess(false);
    setUpdateSuccess(false);
    setRefreshTrigger((prev) => prev + 1);
  }, []);
  // Create owner
  const onCreate = useCallback(
    async (ownerData: IOwnerRequest) => {
      setIsCreating(true);
      setError(null);
      setCreateSuccess(false);
      try {
        const response = await ownerApi.createOwner(ownerData);
        setOwner(response.data);
        setCreateSuccess(true);
        showSnackbar(
          VariantType.success,
          `Owner "${response.data.name}" created successfully`,
          SnackTypes.none
        );
      } catch (err: unknown) {
        console.error('Error creating owner:', err);
        const errorMessage = (err as Error)?.message || 'Failed to create owner';
        setError(errorMessage);
        showSnackbar(VariantType.error, errorMessage, SnackTypes.none);
      } finally {
        setIsCreating(false);
      }
    },
    [showSnackbar, SnackTypes, VariantType]
  );
  // Update owner
  const onUpdate = useCallback(
    async (ownerId: string, ownerData: IOwnerUpdateRequest) => {
      if (!ownerId) {
        setError('Owner ID is required for update');
        return;
      }
      setIsUpdating(true);
      setError(null);
      setUpdateSuccess(false);
      try {
        const response = await ownerApi.updateOwner(ownerId, ownerData);
        setOwner(response.data);
        setUpdateSuccess(true);
        showSnackbar(
          VariantType.success,
          `Owner "${response.data.name}" updated successfully`,
          SnackTypes.none
        );
      } catch (err: unknown) {
        console.error('Error updating owner:', err);
        const errorMessage = (err as Error)?.message || 'Failed to update owner';
        setError(errorMessage);
        showSnackbar(VariantType.error, errorMessage, SnackTypes.none);
      } finally {
        setIsUpdating(false);
      }
    },
    [showSnackbar, SnackTypes, VariantType]
  );
  // Fetch owner by ID (if id is provided)
  useEffect(() => {
    const fetchOwner = async (ownerId: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await ownerApi.getOwner(ownerId);
        setOwner(response.data);
      } catch (err: unknown) {
        console.error('Error fetching owner:', err);
        const errorMessage = (err as Error)?.message || 'Failed to fetch owner';
        setError(errorMessage);
        showSnackbar(VariantType.error, errorMessage, SnackTypes.none);
      } finally {
        setIsLoading(false);
      }
    };
    if (id) {
      fetchOwner(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, refreshTrigger]);
  return {
    // State
    owner,
    isLoading,
    error,
    isCreating,
    isUpdating,
    createSuccess,
    updateSuccess,
    allowCreateOwners,
    // Actions
    onCreate,
    onUpdate,
    onResetCreate,
    onResetUpdate,
    onReset
  };
};

export default useOwner;
